import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { OG_IMAGES } from '@/constants/seoImages';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ShareIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const AffiliateLanding = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    website: '',
    howYouPlan: '',
    agreeToTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.agreeToTerms) {
      toast.error('Please agree to the referral agreement to continue.');
      return;
    }

    setIsSubmitting(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const { data, error } = await supabase.functions.invoke('submit-professional-application', {
        body: {
          profession: 'PROFESSIONAL',
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: '',
          companyName: '',
          website: formData.website,
          bio: formData.howYouPlan,
          linkedinUrl: '',
          licenseNumber: '',
          licenseState: '',
          zipCode: '',
          serviceRadiusMiles: 0,
          specialties: [],
          areasOfExpertise: ['affiliate'],
          professionalCategory: 'other',
          roleTitle: 'Affiliate Partner',
          pagePath: '/affiliate-program',
          referrer: document.referrer || '',
          utmSource: params.get('utm_source') || '',
          utmMedium: params.get('utm_medium') || '',
          utmCampaign: params.get('utm_campaign') || '',
          source: 'affiliate-landing',
        }
      });

      if (error) throw error;

      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success('Application submitted!');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <SEO
          title="Application Received - SeniorThrive Affiliate Program"
          description="Thank you for applying to the SeniorThrive affiliate program."
          noindex
        />
        <div className="min-h-[70vh] flex items-center justify-center px-4">
          <div className="max-w-lg text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircleIcon className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-4">Application Received</h1>
            <p className="text-lg text-muted-foreground mb-6">
              We will review your application and send you an activation email with your
              unique referral link and dashboard access. This usually takes 1–2 business days.
            </p>
            <Button asChild>
              <Link to="/">Back to Homepage</Link>
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Affiliate Program - Earn by Sharing SeniorThrive"
        description="Join the SeniorThrive affiliate program. Earn 20–25% recurring commissions for up to 12 months by referring older adults and families to SeniorThrive."
        keywords="affiliate program, senior care affiliate, referral program, earn commissions, SeniorThrive partner"
        canonical="https://seniorthrive.com/affiliate-program"
        image={OG_IMAGES.affiliate}
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-primary/5 via-background to-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-primary font-bold mb-4 uppercase tracking-wider">
              Affiliate Program
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Earn by Helping Families Thrive at Home
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Share SeniorThrive with your audience and earn recurring commissions every month
              a referred member stays active, for up to 12 months.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground text-center mb-14">
              How It Works
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: ShareIcon,
                  step: '1',
                  title: 'Share your unique link',
                  description: 'We give you a personalized referral link and marketing materials. Share it with your audience however you prefer.',
                },
                {
                  icon: UserGroupIcon,
                  step: '2',
                  title: 'People sign up through you',
                  description: 'When someone clicks your link and joins SeniorThrive, it is automatically tracked to your account.',
                },
                {
                  icon: CurrencyDollarIcon,
                  step: '3',
                  title: 'You earn every month',
                  description: 'You earn a percentage of their subscription every month they stay active, for up to 12 months per referral.',
                },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-14 h-14 bg-primary text-primary-foreground rounded-md flex items-center justify-center mx-auto mb-4 font-bold text-2xl shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Commission Structure */}
      <section className="py-16 lg:py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground text-center mb-4">
              Commission Rates
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-12">
              Transparent, recurring, and paid monthly.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="bg-card p-8 rounded-md border border-border shadow-raised text-center">
                <p className="text-primary font-bold mb-2">Essential Referrals</p>
                <p className="text-5xl font-bold text-foreground mb-2">20%</p>
                <p className="text-muted-foreground mb-4">of net subscription revenue</p>
                <p className="text-muted-foreground">~$5.80/month per active referral</p>
                <p className="text-muted-foreground">for up to 12 months</p>
              </div>
              <div className="bg-card p-8 rounded-md shadow-md text-center">
                <p className="text-primary font-bold mb-2">Family Referrals</p>
                <p className="text-5xl font-bold text-foreground mb-2">25%</p>
                <p className="text-muted-foreground mb-4">of net subscription revenue</p>
                <p className="text-muted-foreground">~$14.75/month per active referral</p>
                <p className="text-muted-foreground">for up to 12 months</p>
              </div>
            </div>

            <div className="bg-card rounded-md p-6 border border-border">
              <div className="grid sm:grid-cols-3 gap-4 text-center">
                <div>
                  <p className="font-bold text-foreground">Monthly payouts</p>
                  <p className="text-muted-foreground">Earnings accrue and are paid monthly</p>
                </div>
                <div>
                  <p className="font-bold text-foreground">12-month window</p>
                  <p className="text-muted-foreground">Earn on each referral for up to a year</p>
                </div>
                <div>
                  <p className="font-bold text-foreground">Real-time tracking</p>
                  <p className="text-muted-foreground">Dashboard shows referrals and earnings</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground text-center mb-12">
              Who This Is For
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Bloggers and content creators in the aging, caregiving, or wellness space',
                'Senior care professionals who recommend tools to families',
                'Community leaders, church groups, and senior center coordinators',
                'Financial advisors, attorneys, or realtors serving older clients',
                'Anyone with a network of older adults or family caregivers',
                'Social media creators focused on family, health, or aging topics',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border/50">
                  <CheckCircleIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-foreground">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/5 to-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-foreground mb-4">Apply Now</h2>
              <p className="text-muted-foreground">
                Tell us a bit about yourself. We review applications within 1–2 business days
                and send your unique referral link and dashboard access via email.
              </p>
            </div>

            <div className="bg-card rounded-md p-8 border border-border shadow-raised">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website or Social Profile</Label>
                  <Input
                    id="website"
                    type="url"
                    placeholder="https://..."
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="howYouPlan">How do you plan to share SeniorThrive?</Label>
                  <textarea
                    id="howYouPlan"
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Blog, social media, email list, in-person, professional network..."
                    value={formData.howYouPlan}
                    onChange={(e) => setFormData(prev => ({ ...prev, howYouPlan: e.target.value }))}
                  />
                </div>

                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    checked={formData.agreeToTerms}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agreeToTerms: checked as boolean }))}
                  />
                  <Label htmlFor="terms" className="text-sm leading-relaxed">
                    I agree to the{' '}
                    <Link to="/referral-agreement" target="_blank" className="text-primary hover:underline">
                      Referral Agreement
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" target="_blank" className="text-primary hover:underline">
                      Privacy Policy
                    </Link>
                  </Label>
                </div>

                <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Apply to the Affiliate Program'}
                </Button>
              </form>
            </div>

            <div className="mt-8 text-center">
              <p className="text-muted-foreground mb-2">Already a partner?</p>
              <Button variant="link" asChild>
                <Link to="/partner-login">Log in to your dashboard</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AffiliateLanding;
